<template>
  <div class="sales-insights-container">
    <InsightsHeader
      :dateValue="dateValue"
      :minDate="minDate"
      :maxDate="maxDate"
      @add="showSlideOut = true"
      @complete="getAndSetSalesOverviewData"
      @dateApply="handleDateApply"
    ></InsightsHeader>
    <InsightsSlideOut
      :show="showSlideOut"
      @cancel="showSlideOut = false"
      @complete="closeSlideOutAndUpdateOverview"
    />
    <DashboardMetricHeader></DashboardMetricHeader>
    <div>
      <div v-if="dashboardLoading" class="sales-insights-loader">
        <Loader
          :size="90"
          :width="9"
        />
      </div>
      <div v-else>
    <div class="dashboard-row top-level-metrics">
        <KpiCard
          v-for="(kpiCard, key) in kpiCardsMap"
          :key="key"
          :metric="kpiCard.metrics[0]"
          :data="getSalesInsightsKpiCardData(key)"
          :tooltip="kpiCard.tooltip"
          :loading="dashboardLoading"
          :error="dashboardError"
          :hideRateChange="!isValidMinDate"
          :customName="getCustomKpiName(kpiCard.metrics[0])"
        />
      </div>
      <div class="tiles-row">
        <PieCard
          v-for="(pieCard, key) in pieCardsMap"
          :key="key"
          :loading="dashboardLoading"
          :error="dashboardError"
          :title="pieCard.title"
          :chartData="getSalesInsightsKpiCardData(key)"
          :chartProps="kpiCardChartProps"
          :tooltip="pieCard.tooltip"
          @iconClick="emitCardLinkClick(key)"
        />
      </div>
      <div
        class="dashboard-row-tile"
      >
        <DataTile
          v-for="(data, key) in topRow"
          :key="key"
          :title="data.title"
          :tooltip="data.tooltip"
          :metrics="data.metrics"
          :dimensions="data.dimensions"
          :initialSort="data.initialSort"
          :data="getSalesInsightsDataTileProp(key)"
          :loading="dashboardLoading"
          :error="dashboardError"
          :shouldBlurValues="!hasSalesDashboard"
          @iconClick="emitTableIconClick"
        ></DataTile>
      </div>
      <div
        class="dashboard-row-tile"
      >
        <TableTile
          v-for="(data, key) in middleTopRow"
          :key="key"
          :loading="dashboardLoading"
          :error="dashboardError"
          :title="data.title"
          :tooltip="data.tooltip"
          :data="getSalesInsightsDataProp(key)"
          :metrics="data.metrics"
          :dimensions="data.dimensions"
          @iconClick="emitTableIconClick"
        />
      </div>
      <div class="performance-card-row">
        <PerformanceCard
          v-for="(insightsSmallTable, key) in bottomRowInsightsSmallTablesMap"
          :key="key"
          :title="insightsSmallTable.title"
          :tooltip="insightsSmallTable.tooltip"
          :tableData="getSalesInsightsDataProp(key)"
          :loading="dashboardLoading"
          :error="dashboardError"
          :shouldBlurValues="!hasSalesDashboard"
        />
      </div>
      <div
        class="dashboard-row-tile"
      >
        <DataTile
          v-for="(data, key) in middleBottomRow"
          :key="key"
          :title="data.title"
          :class="data.title"
          :tooltip="data.tooltip"
          :metrics="data.metrics"
          :dimensions="data.dimensions"
          :initialSort="data.initialSort"
          :data="getSalesInsightsDataTileProp(key)"
          :loading="dashboardLoading"
          :error="dashboardError"
          :shouldBlurValues="!hasSalesDashboard"
          @iconClick="emitTableIconClick"
        />
      </div>
      <div
        class="dashboard-row-tile"
      >
        <DataTile
          v-for="(data, key) in bottomRow"
          :key="key"
          :title="data.title"
          :class="data.title"
          :tooltip="data.tooltip"
          :metrics="data.metrics"
          :dimensions="data.dimensions"
          :initialSort="data.initialSort"
          :data="getSalesInsightsDataTileProp(key)"
          :loading="dashboardLoading"
          :error="dashboardError"
          :shouldBlurValues="!hasSalesDashboard"
          @iconClick="emitTableIconClick"
        />
      </div>
    </div>
    </div>
    <OnboardingTour
      v-if="!dashboardLoading"
      :tourSteps="steps"
      tourName="Sales Insights"
    ></OnboardingTour>
    <slot v-if="!hasSalesDashboard && isNoAccesModalOpen">
      <div class="no-access-overlay"></div>
      <div class="no-access-modal">
        <div class="close-icon" @click="toggleNoAccessModal">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.3002 0.709956C13.1134 0.522704 12.8597 0.417471 12.5952 0.417471C12.3307
                 0.417471 12.077 0.522704 11.8902 0.709956L7.00022 5.58996L2.11021
                 0.699956C1.92338 0.512704 1.66973 0.407471 1.40521 0.407471C1.1407 0.407471
                 0.887046 0.512704 0.700215 0.699956C0.310215 1.08996 0.310215 1.71996 0.700215
                 2.10996L5.59021 6.99996L0.700215 11.89C0.310215 12.28 0.310215 12.91 0.700215
                 13.3C1.09021 13.69 1.72021 13.69 2.11021 13.3L7.00022 8.40996L11.8902 13.3C12.2802
                 13.69 12.9102 13.69 13.3002 13.3C13.6902 12.91 13.6902 12.28 13.3002 11.89L8.41021
                 6.99996L13.3002 2.10996C13.6802 1.72996 13.6802 1.08996 13.3002 0.709956Z"
              fill="#223448"
            />
          </svg>
        </div>
        <svg
          width="35"
          height="35"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style="margin: 15px;"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M3 13C3 11.8954 3.89543 11 5 11H19C20.1046 11 21 11.8954 21 13V20C21
               21.1046 20.1046 22 19 22H5C3.89543 22 3 21.1046 3 20V13Z"
            stroke="#26C7C8"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M7 11V7C7 4.23858 9.23858 2 12 2C14.7614 2 17 4.23858 17 7V11"
            stroke="#26C7C8"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <div>{{ noAccessMessage }}</div>
      </div>
    </slot>
  </div>
</template>
<script>
import { isEqual } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import { salesInsightsTourSteps } from './config/onboardingTourSteps';
import {
  getSalesInsightsCardMapForSection,
  getSalesInsightsCardForTitle,
  salesInsightsCardsMap,
} from './config/insightsCards';
import eventDispatcher from '../../../events/dispatch';
import { getDashboardSalesInsightsData } from '../../../services/dashboard/eventApiService';
import InsightsSlideOut from '../Common/SlideOut.vue';
import InsightsHeader from '../Insights/InsightsHeader.vue';
import DataTile from '../Common/DataTile.vue';
import TableTile from '../Common/TableTile.vue';
import OnboardingTour from './OnboardingTour';
import DashboardMetricHeader
  from '../Common/DashboardMetricHeader/DashboardMetricHeader';
import KpiCard from '../Common/KpiCard/KpiCard.vue';
import PieCard from '../Common/PieCard.vue';
import PerformanceCard from '../Common/Charting/PerformanceCard/PerformanceCard';
import Loader from '../Common/Loader';
import { CommonLogger } from '../../../services/logger';
import {
  getFilterSelectionsTotalCount,
  getErrorMessage,
} from '../../../services/dashboard/dashboardUtils';
import {
  getSelectedFilters,
} from '../../../services/dashboard/filtersService';

export default {
  props: {
    minDate: Date,
    maxDate: Date,
    dateValue: Object,
    startDateStr: String,
    endDateStr: String,
    isValidDateRange: Boolean,
    isValidMinDate: Boolean,
    brandIds: Array,
    brandNames: Array,
    brandIdsStr: String,
  },
  components: {
    InsightsHeader,
    InsightsSlideOut,
    DataTile,
    TableTile,
    OnboardingTour,
    KpiCard,
    PieCard,
    PerformanceCard,
    DashboardMetricHeader,
    Loader,
  },
  data() {
    return {
      dashboardError: false,
      dashboardLoading: true,
      isNoAccesModalOpen: true,
      showSlideOut: false,
      steps: salesInsightsTourSteps,
      viewName: 'Sales Insights',
      noAccessMessage:
        'You do not have access to Sales Insights. ' +
        'Please contact your CSM for assistance.',
      kpiCardsMap: getSalesInsightsCardMapForSection(0),
      pieCardsMap: getSalesInsightsCardMapForSection(1),
      topRow: getSalesInsightsCardMapForSection(2),
      middleTopRow: getSalesInsightsCardMapForSection(3),
      middleBottomRow: getSalesInsightsCardMapForSection(4),
      bottomRowInsightsSmallTablesMap: getSalesInsightsCardMapForSection(5),
      bottomRow: getSalesInsightsCardMapForSection(6),
      kpiCardChartProps: { radius: 190, height: 381, width: 381 },
    };
  },
  beforeMount() {
    this.$store.commit('updateActiveView', this.viewName);
  },
  mounted() {
    if (this.isNewState) {
      this.getAndSetSalesOverviewData();
    }
    else {
      this.dashboardLoading = false;
      this.$store.commit('setIsLoading', false);
    }
  },
  computed: {
    ...mapState({
      portfolioBrands: state => state.dashboard.portfolioBrands,
      salesInsightsData: state => state.dashboard.salesInsightsData,
      currentUser: state => state.main.currentUser,
      dashboardSelectedFilters: state => state.filters.dashboardSelections.filters || [],
      allowedFilters: state => state.filters.allowedFilters.salesInsights,
      showNewBrandSelector: state => state.main.showNewBrandSelector,
    }),
    ...mapGetters({
      selectedBrandIds: 'selectedBrandIds',
      hasSalesDashboard: 'salesDashboardIsAvailable',
    }),
    filters() {
      return getSelectedFilters(
        this.dashboardSelectedFilters,
        this.allowedFilters,
      );
    },
    isValidReportRequest() {
      return this.isValidDateRange
                && this.brandIds
                && this.brandIds.length >= 1;
    },
    stringifiedFilters() {
      return JSON.stringify(this.filters);
    },
    isNewState() {
      return !this.salesInsightsData
                || Object.keys(this.salesInsightsData.uiData)
                  .some(key => this.salesInsightsData.uiData[key] !== this[key]);
    },
    filterDimensions() {
      return this.filters.map(f => f.key);
    },
    filterSelectionsTotalCount() {
      if (!this.filters) return 0;

      return getFilterSelectionsTotalCount(this.filters);
    },
    analyticsObj() {
      return {
        dashboard_view: this.viewName,
        start_date: this.startDateStr,
        end_date: this.endDateStr,
        brand_ids: this.brandIdsStr,
        brand_names: this.brandNames.join(', '),
        brand_count: this.brandIds.length,
        filters: this.stringifiedFilters,
        filter_count: this.filters.length,
        filter_selections_total_count: this.filterSelectionsTotalCount,
        filter_stringified_length: this.stringifiedFilters.length,
        filter_dimensions: this.filterDimensions.join(', '),
        filter_dimensions_count: this.filterDimensions.length,
      };
    },
  },
  methods: {
    closeSlideOutAndUpdateOverview() {
      this.showSlideOut = false;
      this.getAndSetSalesOverviewData();
    },
    handleDateApply(value) {
      const { predefinedRangeValue, range } = value;

      this.$store.commit('updatePredefinedRangeValue', predefinedRangeValue);
      this.$store.commit('updateReportEndDate', range.end);
      this.$store.commit('updateReportStartDate', range.start);
      this.$nextTick(() => {
        this.getAndSetSalesOverviewData();
      });
    },
    async getAndSetSalesOverviewData() {
      if (!this.isValidReportRequest) {
        this.dashboardError = true;
        this.dashboardLoading = false;
        this.$store.commit('setIsLoading', false);

        if (!this.hasSalesDashboard) {
          this.isNoAccesModalOpen = true;
        }

        return;
      }

      this.dashboardError = false;
      this.dashboardLoading = true;
      this.$store.commit('setIsLoading', true);
      let errorMessage = '';

      eventDispatcher('dashboardRequest', {
        ...this.analyticsObj,
      });

      try {
        const data = await this.getSalesDataOverview();

        if (data) {
          data.uiData = {
            brandIdsStr: this.brandIdsStr,
            startDateStr: this.startDateStr,
            endDateStr: this.endDateStr,
            stringifiedFilters: this.stringifiedFilters,
          };
        }

        this.setSalesInsightsData(data);
      } catch (error) {
        this.dashboardError = true;
        errorMessage = getErrorMessage(error);
        this.$store.commit('updateSalesInsightsData', undefined);
        this.$emit('showMessage', { type: 'dashboardError' });
      } finally {
        this.dashboardLoading = false;
        this.$store.commit('setIsLoading', false);
        eventDispatcher('dashboardRequestResponse', {
          ...this.analyticsObj,
          has_error: this.dashboardError,
          error_message: errorMessage,
        });
      }
    },
    async getSalesDataOverview() {
      const token = await this.$auth.getTokenSilently();
      const { data } = await getDashboardSalesInsightsData({
        brandIds: this.brandIds,
        startDate: this.startDateStr,
        endDate: this.endDateStr,
        filters: this.filters,
        activeView: this.viewName,
        token,
      });

      this.logNoDataProps(data);

      return data;
    },
    setSalesInsightsData(data) {
      this.$store.commit('updateSalesInsightsData', data);
    },
    getSalesInsightsDataProp(prop) {
      const avgBasketSize = 'avgBasketSize';
      const avgOrderValue = 'avgOrderValue';
      const avgUnitPrice = 'avgUnitPrice';
      const topRetailers = 'topRetailers';
      const isAvgBasketSize = prop === avgBasketSize;
      const isAvgOrderValue = prop === avgOrderValue;
      const isAvgUnitPrice = prop === avgUnitPrice;

      const data = this.salesInsightsData && this.salesInsightsData[prop];

      if (!isAvgBasketSize && !isAvgOrderValue && !isAvgUnitPrice) {
        return data;
      }

      const isOnlyAmazon = this.salesInsightsData
                && this.salesInsightsData[topRetailers]
                && this.salesInsightsData[topRetailers].length === 1
                && this.salesInsightsData[topRetailers][0].retailer === 'Amazon';

      if (isOnlyAmazon) {
        return null;
      }

      return data;
    },
    getSalesInsightsDataTileProp(prop) {
      const data = this.getSalesInsightsDataProp(prop);

      return data
        ? data.slice(0, 5)
        : [];
    },
    getSalesInsightsKpiCardData(prop) {
      const data = this.getSalesInsightsDataProp(prop);

      return data
        ? { ...data, total: this.getKpiTotal(data) }
        : {};
    },
    getKpiTotal(data) {
      const { measure, rows } = data;
      const row = rows[0] || {};
      const totalKey = `total_${measure}`;
      const forPeriodKey = `${measure}_for_period`;

      return row[totalKey] || row[forPeriodKey] || 0;
    },
    logNoDataProps(data) {
      const noDataProps = this.getNoDataProps(data);

      if (noDataProps.length) {
        const logData = {
          startDate: this.startDateStr,
          endDate: this.endDateStr,
          brandIds: this.brandIds,
          filters: this.stringifiedFilters,
          email: this.currentUser && this.currentUser.email,
          userId: this.currentUser && this.currentUser.id,
        };

        for (const field of noDataProps) {
          const propLogData = {
            ...logData,
            field: field,
          };

          CommonLogger.warn('No data received for sales insights overview field', propLogData);
        }
      }
    },
    getNoDataProps(data) {
      const props = Object.keys(data);
      const metrics = [
        'avgBasketSize',
        'avgOrderValue',
        'avgUnitPrice',
        'totalAttributableSales',
        'totalAttributableUnitsSold',
      ];

      return props.filter(prop => {
        return metrics.includes(prop)
          ? !(data[prop] && data[prop].rows.length)
          : !(data[prop] && data[prop].length);
      });
    },
    emitCardLinkClick(key) {
      const {
        title,
        initialSort,
        dimensions,
        metrics,
        filters,
        view,
      } = salesInsightsCardsMap[key];

      const data = {
        initialSort,
        dimensions,
        metrics,
        filters,
      };

      this.$emit('insightsExpandIconClick', { title, view, data });
    },
    emitTableIconClick(iconData) {
      const { title } = iconData;

      const explorerData = this.getExplorerDataForTitle(title);

      this.emitIconClick(title, explorerData);
    },
    getExplorerDataForTitle(title) {
      const { initialSort, dimensions, metrics, filters } = getSalesInsightsCardForTitle(title);

      return {
        initialSort,
        dimensions,
        metrics,
        filters,
      };
    },
    emitIconClick(title, explorerData) {
      this.$emit('insightsExpandIconClick', {
        title,
        view: 'Explorer',
        data: explorerData,
      });
    },
    toggleNoAccessModal() {
      this.isNoAccesModalOpen = !this.isNoAccesModalOpen;
    },
    getCustomKpiName(metric) {
      return metric === 'attributableSales'
        ? 'Total Attributable Sales'
        : undefined;
    },
  },
  watch: {
    async selectedBrandIds(newIds, oldIds) {
      if (!this.showNewBrandSelector) return;

      if (this.isNewState && !isEqual(newIds, oldIds)) {
        this.$emit('clearSelectedFilters');
        await this.getAndSetSalesOverviewData();
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.dashboard-row-tile {
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  ::v-deep .tile {
    margin: 0px;
    height: auto;
    flex-grow: 1;
  }
}

.dashboard-row {
  display: flex;
  flex-direction: row;
  height: auto;
  flex-wrap: wrap;
}

.tiles-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;

  div {
    flex: 1 1 0;
    height: 100%;
  }
}

.dashboard-row {
  div:first-of-type {
    margin-left: 0px;
  }
  div:last-of-type {
    margin-right: 0px;
  }
}

.no-access-modal {
  position: fixed;
  top: 40%;
  left: 20%;
  right: 20%;
  z-index: 101;
  background-color: white;
  max-width: 100%;
  padding: 40px;
  border-radius: 4px;
  box-shadow: 0 0 100px rgba(0, 0, 0, .50);
  transition: 0.4s;
  font-size: 18px;
}

.no-access-overlay {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background-color: rgba(0, 0 ,0 , 0.4);
  z-index: 100;
  transition: 0.4s;
}

.close-icon {
  position: absolute;
  top: 0;
  right: 0;
  padding: 10px;
  box-sizing: border-box;
  border-radius: 4px;
}

.close-icon:hover {
  background: #F7FAFC;
}

.close-icon:active {
  background: #EDF3F7;
}

.performance-card-row {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

.sales-insights-loader {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 120px;
}
</style>
<style>
.insights-tooltip {
  position: relative;
  cursor: pointer;
}

.insights-tooltip:before {
  display: block;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transform: translate3d(0,-10px,0);
  transition: all .3s ease-in-out;
  background: #1D1D1D;
  max-width: 320px;
  color: #fff;
  border-radius: 20px;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, .5));
  content: attr(data-tooltip);
  font-size: initial;
  text-align: center;
  padding: 1rem;
  bottom: 50px;
  z-index: 100;
}

.insights-tooltip:hover:before {
  opacity: 1;
  transform: translate3d(0,0,0);
}
</style>
